import { ButtonHTMLAttributes, ComponentProps, createRef, FC, forwardRef, MouseEvent } from 'react';
import { handleRefSplit, isNil, Size, useComponentProps, Variable } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { Icon } from '../Icon';
import { ButtonLoader } from './ButtonLoader';
import { ButtonElement, CounterElement, LoaderWrapper } from './style';
import { ButtonType, ButtonVariant } from './types';

type CounterFormatter = (counter: number) => string;

export function createCounterFormatter(maximum: number): CounterFormatter {
    return (counter) => (counter > maximum ? `${maximum}+` : `${counter}`);
}
export const formatCountToMaxNine = createCounterFormatter(9);
export const formatCountToMaxNinetyNine = createCounterFormatter(99);

export interface ButtonBaseProps extends Variable<ButtonVariant> {
    disabled?: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    icon?: IconName;
    counter?: number;
    counterAccessibleText?: string;
    formatCounter?: CounterFormatter;
    forwardedRef?: React.Ref<HTMLButtonElement>;
    'data-test-id'?: string;
    iconSize?: 'small' | 'medium';
    asTag?: string;
    size?: Size;
    wide?: boolean;
    iconPosition?: 'left' | 'right';
    loading?: boolean;
    type: ButtonType;
}

export type ButtonProps = ButtonBaseProps & ButtonHTMLAttributes<HTMLButtonElement>;

const BaseButton = ({
    asTag = 'button',
    children,
    counter,
    counterAccessibleText,
    disabled,
    formatCounter,
    icon,
    iconPosition = 'left',
    onClick,
    size: customSize,
    variant = 'tertiary',
    iconSize = 'small',
    wide = false,
    forwardedRef,
    loading,
    ...other
}: ButtonProps) => {
    const button = createRef<HTMLButtonElement>();
    const { size } = useComponentProps('Button', { size: customSize });

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        if (onClick) {
            onClick(event);
        }
    };

    const iconElement = !!icon && <Icon aria-hidden size={iconSize} value={icon} />;

    const counterBadge = !isNil(counter) ? (
        <Counter counter={counter} formatCounter={formatCounter} variant={variant} aria-label={counterAccessibleText} />
    ) : null;

    const buttonProps: ComponentProps<typeof ButtonElement> = {
        ref: handleRefSplit({ ownRef: button, forwardedRef }),
        size,
        variant,
        onClick: handleClick,
        disabled: loading || disabled,
        'aria-disabled': disabled,
        wide,
        $loading: loading,
        'aria-busy': loading ? true : undefined,
        'aria-live': loading ? 'polite' : undefined,
        ...other,
    };

    return (
        <ButtonElement as={asTag} {...buttonProps}>
            {loading ? (
                <LoaderWrapper>
                    <ButtonLoader buttonVariant={variant} />
                </LoaderWrapper>
            ) : null}
            {iconPosition === 'left' ? iconElement : null}
            {typeof children === 'string' ? <span>{children}</span> : children}
            {counterBadge}
            {iconPosition === 'right' ? iconElement : null}
        </ButtonElement>
    );
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
    <BaseButton {...props} forwardedRef={ref} />
));

interface CounterProps extends Variable<ButtonVariant> {
    counter: number;
    formatCounter?: CounterFormatter;
}

export const Counter: FC<CounterProps> = ({
    counter,
    formatCounter = formatCountToMaxNinetyNine,
    variant,
    ...other
}) => (
    <CounterElement variant={variant} {...other}>
        {formatCounter(counter)}
    </CounterElement>
);
